import { getPostHog } from "../posthog";

export type ErrorSource =
  | "react_boundary"
  | "window_error"
  | "unhandled_rejection";

export interface ReportErrorProps {
  error_source: ErrorSource;
  user_generated?: boolean;
  [key: string]: unknown;
}

const handledErrors = new WeakSet<Error>();

const ignoredErrorClasses = new Set<string>(["ChunkLoadError"]);

const ignoredErrorMessageRegexes: RegExp[] = [
  /^attempted to hard navigate to the same URL/,
  /^Load failed$/,
  /^Unexpected token '<'$/,
  /^\$ is not defined$/,
  /^Can't find variable: \$$/,
  /^Script error\.$/,
];

export function shouldIgnore(error: Error): boolean {
  if (ignoredErrorClasses.has(error.name)) return true;
  if (ignoredErrorMessageRegexes.some((r) => r.test(error.message)))
    return true;

  if (error.stack) {
    const topFrame = error.stack
      .split("\n")
      .slice(1)
      .find((line) => line.trim().length > 0);
    if (topFrame && /DangerouslySetHtmlContent/.test(topFrame)) return true;
  }

  return false;
}

function normalizeError(input: unknown): Error {
  if (input instanceof Error) return input;
  if (typeof input === "string") return new Error(input);
  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error(String(input));
  }
}

export function reportError(error: unknown, props: ReportErrorProps): void {
  if (process.env.NEXT_PUBLIC_POSTHOG_ERROR_TRACKING_ENABLED === "false")
    return;

  const err = normalizeError(error);

  if (handledErrors.has(err)) return;
  handledErrors.add(err);

  if (shouldIgnore(err)) return;

  getPostHog().startSessionRecording({ sampling: true });
  getPostHog().captureException(err, props as Record<string, unknown>);
}

let listenersWired = false;

export function wireGlobalErrorListeners(): void {
  if (typeof window === "undefined") return;
  if (listenersWired) return;
  listenersWired = true;

  window.addEventListener("error", (event) => {
    reportError(event.error ?? new Error(event.message || "window.error"), {
      error_source: "window_error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { error_source: "unhandled_rejection" });
  });
}

export const __testing = {
  resetListenersWired: () => {
    listenersWired = false;
  },
};
